import { Copy, KeyRound, Plus, Power, RadioTower, Store } from "lucide-react";
import { useEffect, useState } from "react";

import { useAuth } from "@/components/AuthProvider";
import { api, formatApiError } from "@/lib/api";


const INITIAL_FORM = { name: "", code: "", bridge_url: "", database_identifier: "" };

export default function StoresPage() {
  const [stores, setStores] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [enrollmentForm, setEnrollmentForm] = useState({ store_id: "", store_name: "" });
  const [enrollmentToken, setEnrollmentToken] = useState(null);
  const [showEnrollmentToken, setShowEnrollmentToken] = useState(true);
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  const [statusLoadingStoreId, setStatusLoadingStoreId] = useState("");
  const { user } = useAuth();
  const isAdmin = user.role === "ADMIN";

  const formatStatusDate = (value) => {
    if (!value) {
      return "Belum ada";
    }

    return new Intl.DateTimeFormat("id-ID", {
      dateStyle: "medium",
      timeStyle: "medium",
    }).format(new Date(value));
  };

  const loadStores = async () => {
    try {
      const response = await api.get("/stores");
      setStores(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  };

  useEffect(() => {
    loadStores();
  }, []);

  const createStore = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    setIsSubmitting(true);
    try {
      await api.post("/stores", form);
      setForm(INITIAL_FORM);
      setNotice("Toko berhasil ditambahkan.");
      await loadStores();
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleStore = async (store) => {
    setError("");
    try {
      await api.patch(`/stores/${store.store_id}`, { is_active: !store.is_active });
      await loadStores();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  };

  const createEnrollmentToken = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    setEnrollmentToken(null);
    setShowEnrollmentToken(true);
    try {
      const response = await api.post("/stores/enrollment-tokens", enrollmentForm);
      setEnrollmentToken(response.data);
      setNotice("Token dibuat. Salin sekali untuk installer sebelum token berakhir.");
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  };

  const copyEnrollmentToken = async () => {
    await navigator.clipboard.writeText(enrollmentToken.enrollment_token);
    setShowEnrollmentToken(false);
    setNotice("Token enrollment disalin. Jangan simpan di chat atau dokumen publik.");
  };

  const loadEnrollmentStatus = async (storeId) => {
    setError("");
    setStatusLoadingStoreId(storeId);
    try {
      const response = await api.get(`/stores/${storeId}/enrollment-status`);
      setEnrollmentStatus(response.data);
    } catch (requestError) {
      setEnrollmentStatus(null);
      setError(formatApiError(requestError));
    } finally {
      setStatusLoadingStoreId("");
    }
  };

  return (
    <main className="workspace" data-testid="stores-page">
      <section className="page-heading">
        <div>
          <p className="eyebrow">REGISTRI TOKO</p>
          <h1 data-testid="stores-page-title">Identitas dan koneksi toko</h1>
        </div>
      </section>
      {error && <p className="form-error" data-testid="stores-error" role="alert">{error}</p>}
      {notice && <p className="form-success" data-testid="stores-success">{notice}</p>}

      {isAdmin && (
        <section className="form-section" aria-labelledby="enrollment-token-title">
          <div className="section-heading">
            <div>
              <p className="eyebrow">ENROLLMENT AMAN</p>
              <h2 id="enrollment-token-title">Token installer sekali pakai</h2>
            </div>
          </div>
          <form className="grid-form" data-testid="create-enrollment-token-form" onSubmit={createEnrollmentToken}>
            <label htmlFor="enrollment-store-id">Store ID unik
              <input
                data-testid="enrollment-store-id-input"
                id="enrollment-store-id"
                onChange={(event) => setEnrollmentForm({ ...enrollmentForm, store_id: event.target.value })}
                pattern="[A-Za-z0-9_-]{2,64}"
                required
                value={enrollmentForm.store_id}
              />
            </label>
            <label htmlFor="enrollment-store-name">Nama toko
              <input
                data-testid="enrollment-store-name-input"
                id="enrollment-store-name"
                onChange={(event) => setEnrollmentForm({ ...enrollmentForm, store_name: event.target.value })}
                required
                value={enrollmentForm.store_name}
              />
            </label>
            <button className="form-action" data-testid="create-enrollment-token-button" type="submit">
              <KeyRound aria-hidden="true" size={17} /> Buat token 15 menit
            </button>
          </form>
          {enrollmentToken && (
            <div className="enrollment-token-result" data-testid="enrollment-token-result">
              <span data-testid="enrollment-token-expiry">
                Berlaku hingga {new Date(enrollmentToken.expires_at).toLocaleTimeString("id-ID")}
              </span>
              <code data-testid="enrollment-token-value">
                {showEnrollmentToken ? enrollmentToken.enrollment_token : "••••••••••••••••••••••••"}
              </code>
              <button
                data-testid="toggle-enrollment-token-visibility"
                onClick={() => setShowEnrollmentToken((current) => !current)}
                type="button"
              >
                {showEnrollmentToken ? "Sembunyikan" : "Tampilkan"}
              </button>
              <button data-testid="copy-enrollment-token-button" onClick={copyEnrollmentToken} type="button">
                <Copy aria-hidden="true" size={16} /> Salin sekali
              </button>
            </div>
          )}
        </section>
      )}

      {isAdmin && (
        <section className="form-section" aria-labelledby="add-store-title">
          <div className="section-heading">
            <div>
              <p className="eyebrow">ADMIN</p>
              <h2 id="add-store-title">Tambahkan toko</h2>
            </div>
          </div>
          <form className="grid-form" data-testid="create-store-form" onSubmit={createStore}>
            <label htmlFor="store-name">Nama toko
              <input data-testid="store-name-input" id="store-name" onChange={(event) => setForm({ ...form, name: event.target.value })} required value={form.name} />
            </label>
            <label htmlFor="store-code">Kode toko
              <input data-testid="store-code-input" id="store-code" onChange={(event) => setForm({ ...form, code: event.target.value })} required value={form.code} />
            </label>
            <label className="wide-field" htmlFor="store-bridge-url">Endpoint Bridge HTTPS
              <input data-testid="store-bridge-url-input" id="store-bridge-url" onChange={(event) => setForm({ ...form, bridge_url: event.target.value })} placeholder="https://kasir-cabang.example.com" required type="url" value={form.bridge_url} />
            </label>
            <label htmlFor="store-database-id">Identitas database internal
              <input data-testid="store-database-id-input" id="store-database-id" onChange={(event) => setForm({ ...form, database_identifier: event.target.value })} value={form.database_identifier} />
            </label>
            <button className="form-action" data-testid="create-store-submit-button" disabled={isSubmitting} type="submit">
              <Plus aria-hidden="true" size={17} /> {isSubmitting ? "Menyimpan…" : "Tambah toko"}
            </button>
          </form>
        </section>
      )}

      <section className="data-section" aria-labelledby="registered-stores-title">
        <div className="section-heading">
          <div>
            <p className="eyebrow">AKSES ANDA</p>
            <h2 id="registered-stores-title">Toko terdaftar</h2>
          </div>
          <span data-testid="registered-store-count">{stores.length} toko</span>
        </div>
        <div className="store-cards" data-testid="registered-store-list">
          {stores.map((store) => (
            <article className="store-card" data-testid={`registered-store-${store.store_id}`} key={store.store_id}>
              <Store aria-hidden="true" size={22} />
              <div>
                <strong>{store.name}</strong>
                <p>{store.code} · {store.database_identifier || "Database tidak diberi label"}</p>
                <small data-testid={`store-endpoint-${store.store_id}`}>
                  {store.bridge_url || "Bridge terdaftar melalui enrollment"}
                </small>
              </div>
              <span className={`state-chip ${store.is_active ? "active" : "inactive"}`}>
                {store.is_active ? "Aktif" : "Nonaktif"}
              </span>
              {isAdmin && (
                <button
                  className="icon-button"
                  data-testid={`toggle-store-${store.store_id}`}
                  onClick={() => toggleStore(store)}
                  title={store.is_active ? "Nonaktifkan toko" : "Aktifkan toko"}
                  type="button"
                >
                  <Power aria-hidden="true" size={16} />
                </button>
              )}
              {isAdmin && (
                <button
                  className="icon-button"
                  data-testid={`enrollment-status-${store.store_id}`}
                  disabled={statusLoadingStoreId === store.store_id}
                  onClick={() => loadEnrollmentStatus(store.store_id)}
                  title="Status enrollment perangkat"
                  type="button"
                >
                  <RadioTower aria-hidden="true" size={16} />
                </button>
              )}
            </article>
          ))}
        </div>
      </section>
      {isAdmin && enrollmentStatus && (
        <section className="data-section" data-testid="enrollment-status-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">REGISTRY PERANGKAT</p>
              <h2 data-testid="binding-panel-title">
                Binding {enrollmentStatus.store_name}
              </h2>
            </div>
          </div>
          <dl className="enrollment-status-grid">
            <div>
              <dt>Store ID</dt>
              <dd data-testid="binding-store-id">{enrollmentStatus.store_id}</dd>
            </div>
            <div>
              <dt>Registry toko</dt>
              <dd data-testid="binding-store-found">
                {enrollmentStatus.store_found ? "Terdaftar" : "Tidak ditemukan"}
              </dd>
            </div>
            <div>
              <dt>Status enrollment</dt>
              <dd data-testid="binding-enrollment-status">
                {enrollmentStatus.enrollment_status}
              </dd>
            </div>
            <div>
              <dt>Binding perangkat</dt>
              <dd data-testid="binding-device-valid">
                {enrollmentStatus.device_binding_status}
              </dd>
            </div>
            <div>
              <dt>Device aktif</dt>
              <dd data-testid="binding-device-count">
                {enrollmentStatus.active_device_count}
              </dd>
            </div>
            <div>
              <dt>Duplikasi store</dt>
              <dd data-testid="binding-duplicate-count">
                {enrollmentStatus.duplicate_store_count}
              </dd>
            </div>
            <div>
              <dt>Last enrollment</dt>
              <dd data-testid="binding-last-enrollment">
                {formatStatusDate(enrollmentStatus.last_enrollment_at)}
              </dd>
            </div>
            <div>
              <dt>Last heartbeat</dt>
              <dd data-testid="binding-last-heartbeat">
                {formatStatusDate(enrollmentStatus.last_heartbeat_at)}
              </dd>
            </div>
            <div>
              <dt>Last seen</dt>
              <dd data-testid="binding-last-seen">
                {formatStatusDate(enrollmentStatus.last_seen)}
              </dd>
            </div>
            <div>
              <dt>Status toko</dt>
              <dd data-testid="binding-connection-status">
                {enrollmentStatus.connection_status === "online" ? "Online" : "Offline"}
              </dd>
            </div>
            <div>
              <dt>Versi Bridge</dt>
              <dd data-testid="binding-bridge-version">
                {enrollmentStatus.bridge_version || "Tidak tersedia"}
              </dd>
            </div>
          </dl>
        </section>
      )}
    </main>
  );
}